<template>
  <b-popover :target="target" show placement="bottom" boundary="viewport">
    <template v-slot:title class="d-flex align-items-start">
      <span> Filter </span>

      <b-button class="close" aria-label="Close" @click="close_popover()">
        <span class="fas fa-times fa-xs" />
      </b-button>
    </template>

    <b-form-radio-group v-show="type !== 'string'" v-model="local_filter_type">
      <b-form-radio value="selected">
        Selected
      </b-form-radio>
      <b-form-radio value="range">
        Range
      </b-form-radio>
    </b-form-radio-group>

    <div style="width:210px">
      <div v-show="local_filter_type === 'selected'">
        <b-form-input
          v-model="search"
          class="mt-4"
          type="search"
          size="sm"
        ></b-form-input>

        <lazy-list-display-y
          :nb="nb_search_results"
          class="mt-2"
          style="height:150px;overflow-y:auto;"
          @fetch_data="fetch_data"
        >
          <template #default="{nby, height, initialized}">
            <filter-search-list-element
              v-if="initialized"
              :key="nby"
              v-bind="arguments_for_nb(nby)"
              :style="height"
              style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"
              @actualize_checked="item_actualize_checked($event, nby)"
            ></filter-search-list-element
            ><filter-search-list-element
              v-else
              :key="nby"
              v-bind="arguments_for_nb(nby)"
            ></filter-search-list-element>
          </template>
        </lazy-list-display-y>
      </div>

      <div v-show="local_filter_type === 'range' && type !== 'string'">
        <columnstore-column-histogram
          :histogram="histogram"
          :cell_width="200"
          :empty_percentage="column_infos.generic.nb_empty / nb_obs"
          :nb_obs="nb_obs"
        />
        <input
          ref="values_slider"
          type="text"
          class="js-range-slider"
          name="my_range"
          value=""
        />
      </div>
    </div>
  </b-popover>
</template>

<script>
import _ from 'lodash'
import $ from 'jquery'

import editor from 'vue2-ace-editor'
import 'ion-rangeslider'

import {
  get_columnstore_column_search_results,
  get_columnstore_column_search_results_length,
  columnstore_toggle_check_filter_element,
  columnstore_select_all_filter,
} from '@/api/subscriptions.js'
import { ColumnstoreColumnFilter } from '@/components/object_model.js'
import { lazy_scroll_requester_base } from '@/components/lazy_list_requester.js'

import ColumnstoreColumnHistogram from '@/components/columnstore_column_histogram.vue'
import LazyListDisplayY from '@/components/lazy_list_display_y.vue'
import FilterSearchListElement from '@/components/filter_search_list_element.vue'

// *****************************************************************************

const block_size = 64

// *****************************************************************************

class lazy_scroll_columnstore_viewer_column_filter_requester extends lazy_scroll_requester_base {
  initialize(available_items, block_size, project_id, poid, name, search_desc) {
    super.initialize(available_items, block_size)
    this.project_id = project_id
    this.poid = poid
    this.name = name
    this.search_desc = search_desc
  }

  async request_blocks(keys) {
    const requested_keys = _.map(keys, function(x) {
      return parseInt(x, 10)
    })
    const data = (await get_columnstore_column_search_results(
      this.project_id,
      this.poid,
      this.name,
      this.search_desc,
      requested_keys
    )).pipe_object
    let res = {}
    for (const [index, slot] of keys.entries()) {
      const slot_start = index * this.block_size
      const slot_stop = slot_start + this.block_size
      let slot_data = data.slice(slot_start, slot_stop)
      res[slot] = slot_data
    }
    return res
  }
}

// *****************************************************************************

export default {
  name: 'ColumnstoreViewerColumnFilter',
  components: {
    editor,
    ColumnstoreColumnHistogram,
    LazyListDisplayY,
    FilterSearchListElement,
  },
  props: ['project_id', 'poid', 'name', 'column_infos', 'nb_obs', 'target'],
  data() {
    return {
      range_slider: undefined,
      // Searched term
      search: '',
      local_filter_type: 'selected',
      selected_values: [],
      range_values: [],
      select_all: false,
      nb_search_results: 0,
      available_items: {},
    }
  },
  computed: {
    _() {
      return _
    },
    show_slider() {
      return this.local_filter_type === 'range' && this.type !== 'string'
    },
    local_filter() {
      let res = undefined
      if (this.selected_values.length !== 0 || this.range_values !== 0) {
        res = new ColumnstoreColumnFilter()
        res.type = this.local_filter_type
        if (this.local_filter_type === 'selected') {
          res.values = _.cloneDeep(this.selected_values)
        } else {
          res.values = this.range_values
        }
      }
      return res
    },
    range() {
      return this.column_infos.generic.range
    },
    histogram() {
      return this.column_infos.histogram
    },
    type() {
      return this.column_infos.generic.category
    },
  },

  watch: {
    show_slider: {
      immediate: true,
      handler() {
        // nextTick is required when the popover is reopened, otherwise
        // the ionrangeslider is not reinitialized
        this.$nextTick(() => {
          this.update_slider()
        })
      },
    },

    async select_all(v) {
      await columnstore_select_all_filter(
        this.project_id,
        this.poid,
        this.name,
        this.search,
        v
      )
      this.reinit_data()
    },

    local_filter: {
      deep: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, this.filter)) {
          this.actualize_filter(nv)
        }
      },
    },

    search: {
      immediate: true,
      async handler(nv) {
        this.refresh_data()
      },
    },
  },

  beforeCreate() {
    this.$options.my_data_requester = new lazy_scroll_columnstore_viewer_column_filter_requester()
  },

  async beforeMount() {
    this.$options.my_data_requester.initialize(
      this.available_items,
      block_size,
      this.project_id,
      this.poid,
      this.name,
      this.search
    )
    this.nb_search_results =
      (await get_columnstore_column_search_results_length(
        this.project_id,
        this.poid,
        this.name,
        this.search
      )).pipe_object + 1
  },

  methods: {
    fetch_data(start, stop) {
      this.$options.my_data_requester.update(start, stop)
    },
    async actualize_checked(global_index) {
      await columnstore_toggle_check_filter_element(
        this.project_id,
        this.poid,
        this.name,
        global_index
      ).pipe_object
      this.refresh_data()
    },

    validate() {
      this.$emit('validate', this.selected_values)
      this.toggle_show_component()
    },
    should_display_option(value) {
      return String(value).includes(String(this.search))
    },
    trim_empty_value(values) {
      return _.filter(values, x => !_.isNaN(x) && !_.isEqual(x, ''))
    },
    close_popover() {
      this.$emit('close_filter_popover')
    },
    create_slider() {
      if (this.type !== 'string' && _.isUndefined(this.range_slider)) {
        let el = $(this.$refs['values_slider'])
        let min_val = this.range[0]
        let max_val = this.range[1]
        let self = this
        el.ionRangeSlider({
          type: 'double',
          min: min_val,
          max: max_val,
          from: self.range_values[0] || min_val,
          to: self.range_values[1] || max_val,
          grid: true,
          grid_num: 10,
          onChange: function(data) {
            self.range_values = [data.from, data.to]
          },
        })
        this.range_slider = el.data('ionRangeSlider')
      }
    },
    destroy_slider() {
      if (!_.isUndefined(this.range_slider)) {
        this.range_slider.destroy()
        this.range_slider = undefined
      }
    },
    update_slider() {
      if (this.show_slider) {
        this.create_slider()
      } else {
        this.destroy_slider()
      }
    },
    actualize_filter(v) {
      this.$emit('actualize_filter', _.cloneDeep(v))
    },
    async refresh_data() {
      this.$options.my_data_requester.refresh()
    },
    async reinit_data() {
      this.$options.my_data_requester.reset()
    },
    arguments_for_nb(nb) {
      let name = 'Select All'
      let checked = this.select_all
      if (nb !== 0) {
        const unb = nb - 1
        const block_nb = Math.floor(unb / block_size)
        const block_index = unb % block_size
        let elt = (this.available_items[block_nb] || [])[block_index] || {}

        name = elt.value
        checked = elt.is_checked
      }
      return { item: { name, checked } }
    },
    item_actualize_checked(nv, nb) {
      if (nb === 0) {
        this.select_all = nv.checked
      } else {
        const unb = nb - 1
        const block_nb = Math.floor(unb / block_size)
        const block_index = unb % block_size
        const elt = (this.available_items[block_nb] || [])[block_index] || {}
        const global_index = elt.global_index
        this.actualize_checked(global_index)
      }
    },
  },
}
</script>
